﻿import { useTranslation } from "../../localization/useTranslation.ts";
import { useUrlProvider } from "../../useUrlProvider.ts";
import React from "react";
import { Box, useTheme } from "@mui/material";
import { UseIncidentLogWidget } from "./IncidentLogWidget.hooks.ts";
import Widget from "../Widget.tsx";
import { DotLegalEmptyState, DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { ResponsiveBar } from "@common/components/nivoCharts/NivoCharts";
import NavigateTo from "../navigateTo/NavigateTo.tsx";
import { IncidentLogWidgetBar } from "./IncidentLogWidget.types.ts";
import EmptyStateIcon from "../svgs/Incidents-empty-state.svg?react";
import DashboardInnerBox from "../DashboardInnerBox";
import { useNavigate } from "react-router";

export interface IIncidentLogWidget {
    isPdf?: boolean;
}

function IncidentLogWidget(props: IIncidentLogWidget) {
    const { translateString } = useTranslation();
    const { data, isLoading, isEnabled, highestNumberOfIncidents } = UseIncidentLogWidget();
    const { getIncidentLogUrl } = useUrlProvider();
    const theme = useTheme();
    const navigate = useNavigate();
    function getYAxisTicks() {
        if (highestNumberOfIncidents === 1) return ["0", "1"];
        if (highestNumberOfIncidents === 2) return ["0", "1", "2"];
        if (highestNumberOfIncidents === 3) return ["0", "1", "2", "3"];
        if (highestNumberOfIncidents === 4) return ["0", "1", "2", "3", "4"];
        if (highestNumberOfIncidents === 5) return ["0", "1", "2", "3", "4", "5"];
        if (highestNumberOfIncidents <= 10) return ["0", "2", "4", "6", "8", "10"];
        if (highestNumberOfIncidents <= 25) return ["0", "5", "10", "15", "20", "25"];
        if (highestNumberOfIncidents <= 50) return ["0", "10", "20", "30", "40", "50"];
        if (highestNumberOfIncidents <= 100) return ["0", "20", "40", "60", "80", "100"];
        return undefined; //returning undefined will fallback to the default graph ticks
    }

    function getGraphTooltip(bar: IncidentLogWidgetBar, index: number) {
        const shouldUseCustomTransform = index > 2;
        return (
            <Box
                sx={(theme) => ({
                    display: "flex",
                    flexDirection: "column",
                    background: "white",
                    width: "200px",
                    paddingLeft: "10px",
                    paddingTop: "5px",
                    paddingRight: "10px",
                    paddingBottom: "5px",
                    justifyContent: "center",
                    borderRadius: "10px",
                    color: theme.palette.primary.dark,
                    boxShadow: "0px 3px 10px #E7E7E7",
                    transform: shouldUseCustomTransform ? "translate(-80px,0)" : "translate(0,0)",
                })}
            >
                <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                    <DotLegalHeader headerStyle={"small"} marginBottom={0} color={"lightgrey"}>
                        {translateString("incidents")}
                    </DotLegalHeader>
                </Box>
                {getGraphTooltipContent(bar.numberOfIncidents, bar.severityName)}
            </Box>
        );
    }

    function getGraphTooltipContent(numberOfIncidents: number, severityName: string) {
        if (!numberOfIncidents) return <React.Fragment></React.Fragment>;
        return (
            numberOfIncidents !== 0 && (
                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", marginLeft: "10px", marginRight: "10px", fontSize: "14px" }}>
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                        <Box sx={{ width: "15px", height: "15px", backgroundColor: theme.palette.primary.main, marginRight: "10px" }}></Box>
                        <Box>{severityName}</Box>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>{numberOfIncidents}</Box>
                    </Box>
                </Box>
            )
        );
    }

    // @ts-expect-error any is ok
    const HorizontalTick = ({ textAnchor, textBaseline, value, x, y }) => {
        const val: string = value;
        const t = val.split(" ").map((val, i) => (
            <tspan dy={i + 15} x={0} key={val}>
                {val}
            </tspan>
        ));

        return (
            <g transform={`translate(${x},${y})`}>
                <text
                    alignmentBaseline={textBaseline}
                    textAnchor={textAnchor}
                    style={{ fontWeight: theme.typography.fontWeightBold, fontSize: 14, fill: theme.palette.primary.dark }}
                >
                    {t}
                </text>
            </g>
        );
    };

    return (
        <Widget disabled={!isEnabled}>
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%", width: props.isPdf ? 500 : "auto" }}>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <DotLegalHeader headerStyle={"medium"} marginBottom={0}>
                        {translateString("incidentLogWidgetHeader").toUpperCase()}
                    </DotLegalHeader>
                </Box>
                {isLoading || highestNumberOfIncidents === 0 ? (
                    <DashboardInnerBox isLoading={isLoading} fullHeight marginTop={1}>
                        <Box sx={{ display: "flex", height: "100%" }}>
                            <DotLegalEmptyState
                                noFixedHeight
                                noPadding
                                textColor={"darkBlue"}
                                text={translateString("noIncidents")}
                                icon={<EmptyStateIcon />}
                            />
                        </Box>
                    </DashboardInnerBox>
                ) : (
                    <Box
                        sx={{
                            width: "100%",
                            height: 0,
                            borderRadius: "20px",
                            flex: "1 1 auto",
                            marginTop: 2,
                            backgroundColor: "white",
                            "g rect:hover": { cursor: "pointer" },
                            paddingTop: "15px",
                            overflow: "hidden",
                        }}
                    >
                        {data && (
                            <ResponsiveBar
                                maxValue={highestNumberOfIncidents}
                                data={data}
                                keys={["numberOfIncidents"]}
                                indexBy="severityName"
                                margin={{ top: 10, right: 10, bottom: 50, left: 60 }}
                                padding={0.3}
                                label={"false"}
                                valueScale={{ type: "linear" }}
                                colors={({ id }) => theme.palette.primary.main}
                                tooltip={(x) => getGraphTooltip(x.data, x.index)}
                                gridYValues={getYAxisTicks()}
                                onClick={(bar) => navigate(bar.data.url)}
                                axisBottom={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legendPosition: "middle",
                                    legendOffset: 32,
                                    renderTick: HorizontalTick,
                                }}
                                axisLeft={{
                                    legend: translateString("incidents"),
                                    legendPosition: "middle",
                                    legendOffset: -45,
                                    tickValues: getYAxisTicks(),
                                }}
                                theme={{
                                    axis: {
                                        legend: {
                                            text: {
                                                fontSize: 16,
                                                fill: theme.palette.primary.dark,
                                                fontWeight: theme.typography.fontWeightBold,
                                            },
                                        },
                                        ticks: {
                                            text: {
                                                fontSize: 14,
                                                fill: theme.palette.primary.dark,
                                                fontWeight: theme.typography.fontWeightBold,
                                            },
                                        },
                                    },
                                }}
                            />
                        )}
                    </Box>
                )}
                {!props.isPdf && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <NavigateTo color="purple" text={translateString("goToIncidentLog")} to={getIncidentLogUrl()} />
                    </Box>
                )}
            </Box>
        </Widget>
    );
}

export default IncidentLogWidget;
